/**
 * Split Chat View Component
 *
 * Displays two chat panels side by side with a draggable divider.
 * Allows users to view and interact with two threads simultaneously.
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useRef, useCallback } from "react";
import type { AIModel } from "@/lib/apiKeyStorage";
import type { UserTier } from "@/hooks/useUserTier";
import type { Thread } from "@/types/chat";
import type { MessageType, QuickSendTarget } from "@/types/splitView";
import ChatPanel from "./ChatPanel";
interface SplitChatViewProps {
  leftThreadId: string | null;
  rightThreadId: string | null;
  threads: Thread[];
  userId: string | undefined;
  userTier: UserTier;
  selectedModel: AIModel;
  onModelChange: (model: AIModel) => void;
  onThreadTitleUpdated?: () => void;
  onCreateThread: () => Promise<string | null>;
  onForkThread?: (fromThreadId: string, fromMessageId: string, newTitle?: string) => Promise<string | null>;
  onClose: () => void;
  onSwapPanels: () => void;
  onSelectLeftThread: (threadId: string) => void;
  onSelectRightThread: (threadId: string) => void;
  isFeatureEnabled?: (feature: string) => boolean;
  initialSplitRatio?: number;
  crossChatEnabled?: boolean;
  onToggleCrossChat?: () => void;
  leftPanelName: string;
  rightPanelName: string;
  onLeftPanelNameChange: (name: string) => void;
  onRightPanelNameChange: (name: string) => void;
  messageType: MessageType;
  onMessageTypeChange: (type: MessageType) => void;
}
export default function SplitChatView(t0) {
  const $ = _c(144);
  const {
    leftThreadId,
    rightThreadId,
    threads,
    userId,
    userTier,
    selectedModel,
    onThreadTitleUpdated,
    onCreateThread,
    onForkThread,
    onClose,
    onSwapPanels,
    onSelectLeftThread,
    onSelectRightThread,
    isFeatureEnabled,
    initialSplitRatio: t1,
    crossChatEnabled: t2,
    onToggleCrossChat,
    leftPanelName,
    rightPanelName,
    onLeftPanelNameChange,
    onRightPanelNameChange,
    messageType,
    onMessageTypeChange
  } = t0;
  const initialSplitRatio = t1 === undefined ? 50 : t1;
  const crossChatEnabled = t2 === undefined ? false : t2;
  const [splitRatio, setSplitRatio] = useState(initialSplitRatio);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const [leftModel, setLeftModel] = useState(selectedModel);
  const [rightModel, setRightModel] = useState(selectedModel);
  let t3;
  if ($[0] !== leftThreadId || $[1] !== threads) {
    t3 = threads.find(t => t.id === leftThreadId) || null;
    $[0] = leftThreadId;
    $[1] = threads;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const leftThread = t3;
  let t4;
  if ($[3] !== rightThreadId || $[4] !== threads) {
    t4 = threads.find(t_0 => t_0.id === rightThreadId) || null;
    $[3] = rightThreadId;
    $[4] = threads;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const rightThread = t4;
  const leftSendMessageRef = useRef(null);
  const rightSendMessageRef = useRef(null);
  let t5;
  if ($[6] !== crossChatEnabled || $[7] !== leftPanelName || $[8] !== messageType || $[9] !== rightPanelName || $[10] !== rightThreadId) {
    t5 = async content => {
      if (!crossChatEnabled || !rightThreadId || !rightSendMessageRef.current) {
        return;
      }
      if (messageType === "chat") {
        sessionStorage.removeItem(`chatMode_${rightThreadId}_responded`);
      }
      const messageTypeLabel = messageType === "instruction" ? "Instruction" : "Chat";
      console.log(`[Cross-Chat] ${leftPanelName} sending ${messageTypeLabel.toLowerCase()} to ${rightPanelName}`);
      const relayMessage = `**[${messageTypeLabel} from ${leftPanelName}]**\n\n${content}`;
      ;
      try {
        await rightSendMessageRef.current(relayMessage, []);
      } catch (t6) {
        const error = t6;
        console.error(`[Cross-Chat] Failed to send to ${rightPanelName}:`, error);
      }
    };
    $[6] = crossChatEnabled;
    $[7] = leftPanelName;
    $[8] = messageType;
    $[9] = rightPanelName;
    $[10] = rightThreadId;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleLeftAIResponse = t5;
  let t6;
  if ($[12] !== crossChatEnabled || $[13] !== leftPanelName || $[14] !== leftThreadId || $[15] !== messageType || $[16] !== rightPanelName || $[17] !== rightThreadId) {
    t6 = async content_0 => {
      if (!crossChatEnabled || !leftThreadId || !leftSendMessageRef.current) {
        return;
      }
      if (messageType === "instruction") {
        const isQuestion = content_0.includes("?") || content_0.toLowerCase().includes("clarif") || content_0.toLowerCase().includes("could you") || content_0.toLowerCase().includes("can you") || content_0.toLowerCase().includes("please confirm") || content_0.toLowerCase().includes("not sure") || content_0.toLowerCase().includes("unclear");
        if (!isQuestion) {
          console.log(`[Cross-Chat] INSTRUCTION MODE: ${rightPanelName} response blocked (not a question)`);
          return;
        }
        console.log(`[Cross-Chat] INSTRUCTION MODE: ${rightPanelName} asking clarifying question to ${leftPanelName}`);
        const relayMessage_0 = `**[Question from ${rightPanelName}]**\n\n${content_0}`;
        ;
        try {
          await leftSendMessageRef.current(relayMessage_0, []);
        } catch (t7) {
          const error_0 = t7;
          console.error(`[Cross-Chat] Failed to send question to ${leftPanelName}:`, error_0);
        }
      } else {
        if (messageType === "chat") {
          const hasResponded = sessionStorage.getItem(`chatMode_${rightThreadId}_responded`);
          if (hasResponded) {
            console.log(`[Cross-Chat] CHAT MODE: ${rightPanelName} already responded once, blocking subsequent response`);
            return;
          }
          console.log(`[Cross-Chat] CHAT MODE: ${rightPanelName} sending first response to ${leftPanelName}`);
          const relayMessage_1 = `**[Response from ${rightPanelName}]**\n\n${content_0}`;
          ;
          try {
            await leftSendMessageRef.current(relayMessage_1, []);
            sessionStorage.setItem(`chatMode_${rightThreadId}_responded`, "true");
          } catch (t8) {
            const error_1 = t8;
            console.error(`[Cross-Chat] Failed to send response to ${leftPanelName}:`, error_1);
          }
        }
      }
    };
    $[12] = crossChatEnabled;
    $[13] = leftPanelName;
    $[14] = leftThreadId;
    $[15] = messageType;
    $[16] = rightPanelName;
    $[17] = rightThreadId;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const handleRightAIResponse = t6;
  let t7;
  if ($[19] !== onCreateThread || $[20] !== onSelectLeftThread) {
    t7 = async () => {
      const newThreadId = await onCreateThread();
      if (newThreadId) {
        onSelectLeftThread(newThreadId);
      }
      return newThreadId;
    };
    $[19] = onCreateThread;
    $[20] = onSelectLeftThread;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const handleCreateLeftThread = t7;
  let t8;
  if ($[22] !== onCreateThread || $[23] !== onSelectRightThread) {
    t8 = async () => {
      const newThreadId_0 = await onCreateThread();
      if (newThreadId_0) {
        onSelectRightThread(newThreadId_0);
      }
      return newThreadId_0;
    };
    $[22] = onCreateThread;
    $[23] = onSelectRightThread;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const handleCreateRightThread = t8;
  let t9;
  if ($[25] !== rightPanelName || $[26] !== rightThreadId) {
    t9 = rightThreadId ? [{
      panelId: "right",
      panelName: rightPanelName,
      threadId: rightThreadId
    }] : [];
    $[25] = rightPanelName;
    $[26] = rightThreadId;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const leftQuickSendTargets = t9;
  let t10;
  if ($[28] !== leftPanelName || $[29] !== leftThreadId) {
    t10 = leftThreadId ? [{
      panelId: "left",
      panelName: leftPanelName,
      threadId: leftThreadId
    }] : [];
    $[28] = leftPanelName;
    $[29] = leftThreadId;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const rightQuickSendTargets = t10;
  let t11;
  if ($[31] !== leftPanelName || $[32] !== rightPanelName) {
    t11 = async (targetPanelId, message) => {
      if (targetPanelId === "right" && rightSendMessageRef.current) {
        console.log(`[QuickSend] Sending from ${leftPanelName} to ${rightPanelName}:`, message);
        ;
        try {
          await rightSendMessageRef.current(message, []);
        } catch (t12) {
          const error_2 = t12;
          console.error(`[QuickSend] Failed to send to ${rightPanelName}:`, error_2);
        }
      }
    };
    $[31] = leftPanelName;
    $[32] = rightPanelName;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const handleLeftQuickSend = t11;
  let t12;
  if ($[34] !== leftPanelName || $[35] !== rightPanelName) {
    t12 = async (targetPanelId_0, message_0) => {
      if (targetPanelId_0 === "left" && leftSendMessageRef.current) {
        console.log(`[QuickSend] Sending from ${rightPanelName} to ${leftPanelName}:`, message_0);
        ;
        try {
          await leftSendMessageRef.current(message_0, []);
        } catch (t13) {
          const error_3 = t13;
          console.error(`[QuickSend] Failed to send to ${leftPanelName}:`, error_3);
        }
      }
    };
    $[34] = leftPanelName;
    $[35] = rightPanelName;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const handleRightQuickSend = t12;
  let t13;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = e => {
      e.preventDefault();
      setIsDragging(true);
    };
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  const handleMouseDown = t13;
  let t14;
  if ($[38] !== isDragging) {
    t14 = e_0 => {
      if (!isDragging || !containerRef.current) {
        return;
      }
      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const newRatio = (e_0.clientX - containerRect.left) / containerRect.width * 100;
      setSplitRatio(Math.max(20, Math.min(80, newRatio)));
    };
    $[38] = isDragging;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  const handleMouseMove = t14;
  let t15;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      setIsDragging(false);
    };
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  const handleMouseUp = t15;
  let t16;
  if ($[41] !== handleMouseMove || $[42] !== isDragging) {
    t16 = () => {
      if (isDragging) {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        document.body.style.userSelect = "none";
        document.body.style.cursor = "ew-resize";
        return () => {
          document.removeEventListener("mousemove", handleMouseMove);
          document.removeEventListener("mouseup", handleMouseUp);
          document.body.style.userSelect = "";
          document.body.style.cursor = "";
        };
      }
    };
    $[41] = handleMouseMove;
    $[42] = isDragging;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== isDragging) {
    t17 = [isDragging];
    $[44] = isDragging;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  React.useEffect(t16, t17);
  let t18;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <h1 className="text-xl font-bold text-foreground">Split View</h1>;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== onSwapPanels) {
    t20 = <button onClick={onSwapPanels} className="rounded-lg border border-white/40 bg-white/50 px-3 py-1.5 text-sm font-medium text-foreground hover:bg-white/70 transition-colors" title="Swap panels">{t19}</button>;
    $[48] = onSwapPanels;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <button onClick={() => setSplitRatio(50)} className="rounded-lg border border-white/40 bg-white/50 px-3 py-1.5 text-sm font-medium text-foreground hover:bg-white/70 transition-colors" title="Reset to 50/50 split">Reset Split</button>;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== crossChatEnabled || $[52] !== onToggleCrossChat) {
    t22 = onToggleCrossChat && <button onClick={onToggleCrossChat} className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors flex items-center gap-2 ${crossChatEnabled ? "border-mint bg-mint text-foreground hover:bg-mint/80" : "border-white/40 bg-white/50 text-foreground hover:bg-white/70"}`} title="Enable workflow mode: Main chat can send instructions to New chat"><svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>{crossChatEnabled ? "Workflow Mode ON" : "Workflow Mode OFF"}</button>;
    $[51] = crossChatEnabled;
    $[52] = onToggleCrossChat;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== crossChatEnabled || $[55] !== messageType || $[56] !== onMessageTypeChange) {
    t23 = crossChatEnabled && <div className="flex items-center gap-1 rounded-lg border border-white/40 bg-white/50 overflow-hidden"><button onClick={() => onMessageTypeChange("instruction")} className={`px-3 py-1.5 text-sm font-medium transition-colors ${messageType === "instruction" ? "bg-sky text-foreground" : "text-foreground hover:bg-white/70"}`} title="Instruction mode: Task-oriented, minimal response">Instruction</button><button onClick={() => onMessageTypeChange("chat")} className={`px-3 py-1.5 text-sm font-medium transition-colors ${messageType === "chat" ? "bg-sky text-foreground" : "text-foreground hover:bg-white/70"}`} title="Chat mode: Conversational, expects response">Chat</button></div>;
    $[54] = crossChatEnabled;
    $[55] = messageType;
    $[56] = onMessageTypeChange;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== t20 || $[59] !== t22 || $[60] !== t23) {
    t24 = <div className="flex items-center gap-4">{t18}<div className="flex items-center gap-2">{t20}{t21}{t22}{t23}</div></div>;
    $[58] = t20;
    $[59] = t22;
    $[60] = t23;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== onClose) {
    t25 = <button onClick={onClose} className="rounded-lg border border-white/40 bg-white/50 px-4 py-2 text-sm font-medium text-foreground hover:bg-white/70 transition-colors">Exit Split View</button>;
    $[62] = onClose;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== t24 || $[65] !== t25) {
    t26 = <div className="flex items-center justify-between border-b border-white/30 bg-white/40 backdrop-blur-md px-4 py-3">{t24}{t25}</div>;
    $[64] = t24;
    $[65] = t25;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  const t27 = `${splitRatio}%`;
  let t28;
  if ($[67] !== t27) {
    t28 = {
      width: t27
    };
    $[67] = t27;
    $[68] = t28;
  } else {
    t28 = $[68];
  }
  const t29 = leftThreadId || "";
  let t30;
  if ($[69] !== onSelectLeftThread) {
    t30 = e_1 => onSelectLeftThread(e_1.target.value);
    $[69] = onSelectLeftThread;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <option value="">Select a thread...</option>;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== threads) {
    t32 = threads.map(_temp);
    $[72] = threads;
    $[73] = t32;
  } else {
    t32 = $[73];
  }
  let t33;
  if ($[74] !== t29 || $[75] !== t30 || $[76] !== t32) {
    t33 = <div className="border-b border-white/30 bg-white/30 backdrop-blur-sm px-4 py-2"><select value={t29} onChange={t30} className="w-full rounded-lg border border-white/40 bg-white/60 px-3 py-1.5 text-sm text-foreground focus:border-sky focus:outline-none focus:ring-1 focus:ring-sky">{t31}{t32}</select></div>;
    $[74] = t29;
    $[75] = t30;
    $[76] = t32;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  let t34;
  if ($[78] !== crossChatEnabled || $[79] !== handleCreateLeftThread || $[80] !== handleLeftAIResponse || $[81] !== handleLeftQuickSend || $[82] !== isFeatureEnabled || $[83] !== leftModel || $[84] !== leftPanelName || $[85] !== leftQuickSendTargets || $[86] !== leftThread || $[87] !== leftThreadId || $[88] !== messageType || $[89] !== onForkThread || $[90] !== onLeftPanelNameChange || $[91] !== onThreadTitleUpdated || $[92] !== rightPanelName || $[93] !== userId || $[94] !== userTier) {
    t34 = leftThreadId ? <ChatPanel threadId={leftThreadId} thread={leftThread} userId={userId} userTier={userTier} selectedModel={leftModel} onModelChange={setLeftModel} onThreadTitleUpdated={onThreadTitleUpdated} onCreateThread={handleCreateLeftThread} onForkThread={onForkThread} isFeatureEnabled={isFeatureEnabled} showHeader={false} crossChatEnabled={crossChatEnabled} onAIResponse={handleLeftAIResponse} exposeSendMessage={sendFn => {
      leftSendMessageRef.current = sendFn;
    }} isMainChat={true} panelName={leftPanelName} otherPanelName={rightPanelName} onPanelNameChange={onLeftPanelNameChange} messageType={messageType} quickSendTargets={leftQuickSendTargets} onQuickSend={handleLeftQuickSend} currentPanelId="left" /> : <div className="flex h-full items-center justify-center text-foreground/50"><div className="text-center"><p className="text-lg font-medium">No thread selected</p><p className="mt-2 text-sm">Select a thread from the dropdown above</p></div></div>;
    $[78] = crossChatEnabled;
    $[79] = handleCreateLeftThread;
    $[80] = handleLeftAIResponse;
    $[81] = handleLeftQuickSend;
    $[82] = isFeatureEnabled;
    $[83] = leftModel;
    $[84] = leftPanelName;
    $[85] = leftQuickSendTargets;
    $[86] = leftThread;
    $[87] = leftThreadId;
    $[88] = messageType;
    $[89] = onForkThread;
    $[90] = onLeftPanelNameChange;
    $[91] = onThreadTitleUpdated;
    $[92] = rightPanelName;
    $[93] = userId;
    $[94] = userTier;
    $[95] = t34;
  } else {
    t34 = $[95];
  }
  let t35;
  if ($[96] !== t28 || $[97] !== t33 || $[98] !== t34) {
    t35 = <div className="overflow-hidden" style={t28}>{t33}{t34}</div>;
    $[96] = t28;
    $[97] = t33;
    $[98] = t34;
    $[99] = t35;
  } else {
    t35 = $[99];
  }
  const t36 = `relative w-1 cursor-ew-resize bg-white/40 hover:bg-sky transition-colors ${isDragging ? "bg-sky" : ""}`;
  let t37;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <div className="absolute inset-y-0 -left-1 -right-1" />;
    $[100] = t37;
  } else {
    t37 = $[100];
  }
  let t38;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-1"><div className="h-1 w-1 rounded-full bg-foreground/30" /><div className="h-1 w-1 rounded-full bg-foreground/30" /><div className="h-1 w-1 rounded-full bg-foreground/30" /></div>;
    $[101] = t38;
  } else {
    t38 = $[101];
  }
  let t39;
  if ($[102] !== t36) {
    t39 = <div className={t36} onMouseDown={handleMouseDown}>{t37}{t38}</div>;
    $[102] = t36;
    $[103] = t39;
  } else {
    t39 = $[103];
  }
  const t40 = `${100 - splitRatio}%`;
  let t41;
  if ($[104] !== t40) {
    t41 = {
      width: t40
    };
    $[104] = t40;
    $[105] = t41;
  } else {
    t41 = $[105];
  }
  const t42 = rightThreadId || "";
  let t43;
  if ($[106] !== onSelectRightThread) {
    t43 = e_2 => onSelectRightThread(e_2.target.value);
    $[106] = onSelectRightThread;
    $[107] = t43;
  } else {
    t43 = $[107];
  }
  let t44;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <option value="">Select a thread...</option>;
    $[108] = t44;
  } else {
    t44 = $[108];
  }
  let t45;
  if ($[109] !== threads) {
    t45 = threads.map(_temp2);
    $[109] = threads;
    $[110] = t45;
  } else {
    t45 = $[110];
  }
  let t46;
  if ($[111] !== t42 || $[112] !== t43 || $[113] !== t45) {
    t46 = <div className="border-b border-white/30 bg-white/30 backdrop-blur-sm px-4 py-2"><select value={t42} onChange={t43} className="w-full rounded-lg border border-white/40 bg-white/60 px-3 py-1.5 text-sm text-foreground focus:border-sky focus:outline-none focus:ring-1 focus:ring-sky">{t44}{t45}</select></div>;
    $[111] = t42;
    $[112] = t43;
    $[113] = t45;
    $[114] = t46;
  } else {
    t46 = $[114];
  }
  let t47;
  if ($[115] !== crossChatEnabled || $[116] !== handleCreateRightThread || $[117] !== handleRightAIResponse || $[118] !== handleRightQuickSend || $[119] !== isFeatureEnabled || $[120] !== leftPanelName || $[121] !== messageType || $[122] !== onForkThread || $[123] !== onRightPanelNameChange || $[124] !== onThreadTitleUpdated || $[125] !== rightModel || $[126] !== rightPanelName || $[127] !== rightQuickSendTargets || $[128] !== rightThread || $[129] !== rightThreadId || $[130] !== userId || $[131] !== userTier) {
    t47 = rightThreadId ? <ChatPanel threadId={rightThreadId} thread={rightThread} userId={userId} userTier={userTier} selectedModel={rightModel} onModelChange={setRightModel} onThreadTitleUpdated={onThreadTitleUpdated} onCreateThread={handleCreateRightThread} onForkThread={onForkThread} isFeatureEnabled={isFeatureEnabled} showHeader={false} crossChatEnabled={crossChatEnabled} onAIResponse={handleRightAIResponse} exposeSendMessage={sendFn_0 => {
      rightSendMessageRef.current = sendFn_0;
    }} isMainChat={false} panelName={rightPanelName} otherPanelName={leftPanelName} onPanelNameChange={onRightPanelNameChange} messageType={messageType} quickSendTargets={rightQuickSendTargets} onQuickSend={handleRightQuickSend} currentPanelId="right" /> : <div className="flex h-full items-center justify-center text-foreground/50"><div className="text-center"><p className="text-lg font-medium">No thread selected</p><p className="mt-2 text-sm">Select a thread from the dropdown above</p></div></div>;
    $[115] = crossChatEnabled;
    $[116] = handleCreateRightThread;
    $[117] = handleRightAIResponse;
    $[118] = handleRightQuickSend;
    $[119] = isFeatureEnabled;
    $[120] = leftPanelName;
    $[121] = messageType;
    $[122] = onForkThread;
    $[123] = onRightPanelNameChange;
    $[124] = onThreadTitleUpdated;
    $[125] = rightModel;
    $[126] = rightPanelName;
    $[127] = rightQuickSendTargets;
    $[128] = rightThread;
    $[129] = rightThreadId;
    $[130] = userId;
    $[131] = userTier;
    $[132] = t47;
  } else {
    t47 = $[132];
  }
  let t48;
  if ($[133] !== t41 || $[134] !== t46 || $[135] !== t47) {
    t48 = <div className="overflow-hidden" style={t41}>{t46}{t47}</div>;
    $[133] = t41;
    $[134] = t46;
    $[135] = t47;
    $[136] = t48;
  } else {
    t48 = $[136];
  }
  let t49;
  if ($[137] !== t35 || $[138] !== t39 || $[139] !== t48) {
    t49 = <div ref={containerRef} className="flex flex-1 overflow-hidden">{t35}{t39}{t48}</div>;
    $[137] = t35;
    $[138] = t39;
    $[139] = t48;
    $[140] = t49;
  } else {
    t49 = $[140];
  }
  let t50;
  if ($[141] !== t26 || $[142] !== t49) {
    t50 = <div className="flex h-full flex-col overflow-hidden">{t26}{t49}</div>;
    $[141] = t26;
    $[142] = t49;
    $[143] = t50;
  } else {
    t50 = $[143];
  }
  return t50;
}
function _temp2(thread_0) {
  return <option key={thread_0.id} value={thread_0.id}>{thread_0.title}</option>;
}
function _temp(thread) {
  return <option key={thread.id} value={thread.id}>{thread.title}</option>;
}